import { startOfDay, sub } from 'date-fns';
import {
  Button,
  FilterType,
  Table,
  TableFilter,
  Flex,
  BoxV2,
  useFilters,
  useTable,
} from 'portal-commons';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container } from '@material-ui/core';

import {
  fetchEventHistory,
  downloadEventHistory,
  getEventTypeOptions,
} from '../apiServices';
import EventsListingRow from '../components/EventsListingRow';
import { MAX_EVENT_RECORDS } from '../constants';
import { Loader, FilterToggleButton } from '../../../shared_elements';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '../../../shared_elements/actions';
import { toastFlashMessage } from '../../../utils';
import { generateAndDownloadFile } from '../../../utils/download';
import { globalGetService } from '../../../utils/globalApiServices';
import { convertTimeWithTimezone } from '../../../utils/time';
import { getAuthenticatedUserSuggestions } from '@/apis/services';

import '../../../assets/styles/events-listing-module.scss';
import { constantizeAuthenticatedUser } from '@/utils/string';

const Today = startOfDay(new Date());
const DateRangePresets = [
  {
    label: 'Today',
    start: Today,
    end: Today,
  },
  {
    label: 'Last 7 Days',
    start: sub(Today, { days: 7 }),
    end: Today,
  },
  {
    label: 'Last 1 Month',
    start: sub(Today, { months: 1 }),
    end: Today,
  },
  {
    label: 'Last 6 Months',
    start: sub(Today, { months: 6 }),
    end: Today,
  },
];

const FILTER_CONFIGS = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Type Campaign ID',
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Type Brand ID',
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Type Brand DBA Name',
    suggestions: [],
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Type CSP DBA Name',
    suggestions: [],
  },
  eventType: {
    type: FilterType.Dropdown,
    label: 'Event Type',
    options: [],
    width: 250,
  },
  dateRange: {
    type: FilterType.DateRange,
    label: 'Date Range',
    placeholder: 'Select date range',
    delayUpdate: false,
    dateRangePresets: DateRangePresets,
  },
  authenticatedUser: {
    type: FilterType.Text,
    label: 'User',
    placeholder: 'Type User',
    suggestions: [],
    width: 250,
  },
};

const headRows = [
  { label: 'EVENT', id: 'eventType', sortable: true },
  { label: 'CATEGORY', id: 'categoryType', sortable: true },
  { label: 'CAMPAIGN ID', id: 'campaignUid', sortable: true },
  { label: 'BRAND ID', id: 'brandUid', sortable: true },
  { label: 'DATE & TIME', id: 'createDate', sortable: true },
  { label: 'USER', id: 'authenticatedUser', sortable: false },
  { label: 'DESCRIPTION', id: 'description', sortable: false },
];

const fetchBrandSuggestions = async (query) => {
  return globalGetService('dca/brands/suggestions', {
    ...query,
    limit: 20,
    status: 'ACTIVE',
  }).then((response) => {
    if (response?.ok) {
      return response.data?.map((d) => d?.brandName) ?? [];
    }
  });
};

const fetchCspSuggestions = async (query = {}) => {
  return globalGetService('dca/csp/suggestions', { ...query, limit: 20 }).then(
    (response) => {
      if (response?.ok) {
        return response.data?.map((d) => d.cspName) ?? [];
      }
    }
  );
};

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => fetchBrandSuggestions({ prefix: value }),
    },
    cspName: {
      minLength: 2,
      load: (value: string) => fetchCspSuggestions({ prefix: value }),
    },
    authenticatedUser: {
      minLength: 2,
      load: (value: string) => getAuthenticatedUserSuggestions(value),
    },
  },
};

const EventsList = () => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const dispatch = useDispatch();

  const filter = useFilters({
    configs: FILTER_CONFIGS,
    loaders: FILTER_LOADERS,
  });

  const table = useTable(
    async () => {
      const queries = getQueries();
      const response = await fetchEventHistory(queries);
      if (response) {
        return {
          data: response.records,
          page: response.page,
          rowsPerPage: response.recordsPerPage,
          total: response.totalRecords,
        };
      }
      return {
        data: [],
        page: 1,
        rowsPerPage: 10,
        total: 0,
      };
    },
    {
      cappingTotal: MAX_EVENT_RECORDS,
      deps: [filter.appliedFilters, filter.additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const isExceedingLimit = table.total > MAX_EVENT_RECORDS;

  useEffect(() => {
    document.title = 'The Campaign Registry - Events';
    getEventTypeOptions().then((options) => {
      filter.setOptions('eventType', options);
    });
  }, []);

  const getQueries = () => {
    const authenticatedUser = constantizeAuthenticatedUser(
      filter.appliedFilters.authenticatedUser
    );
    return dateRangePreprocess({
      ...table.filter,
      ...filter.appliedFilters,
      ...filter.additionalFilters,
      ...(authenticatedUser ? { authenticatedUser } : {}),
    });
  };

  const dateRangePreprocess = (queries: Record<string, unknown>) => {
    if (queries.hasOwnProperty('dateRange')) {
      const dates = (queries.dateRange as string)
        .replace(/\s/gi, '')
        .split('-');
      if (dates.length === 2) {
        queries.startDate = convertTimeWithTimezone(dates[0], 'YYYY-MM-DD');
        queries.endDate = convertTimeWithTimezone(dates[1], 'YYYY-MM-DD');
      }
      delete queries.dateRange;
    }
    return queries;
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  const handleDownload = async () => {
    setLoadingMessage('We are generating the document');
    const { page, ...queries } = await getQueries();
    const response = await downloadEventHistory(queries);
    if (response) {
      const file = new File([response], 'event-history.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download event history', 'error');
    }
    clearLoadingMessage();
  };

  const setLoadingMessage = (message: string) =>
    dispatch({ type: SET_LOADING_MESSAGE, payload: message });

  const clearLoadingMessage = () => dispatch({ type: CLEAR_LOADING_MESSAGE });

  return (
    <section
      className="events-listing-section"
      style={{ padding: '0px' }}
      data-testid="eventsList"
    >
      {table.pageLoading ? (
        <Loader />
      ) : (
        <>
          <Flex
            sx={{
              alignItems: 'center',
              mb: '8px',
              fontWeight: 600,
              fontSize: 'H600',
              lineHeight: '32px',
              color: 't.black90',
            }}
          >
            <span>
              {table.total !== 0 ? `${table.total} Events` : 'Events'}
            </span>
            <BoxV2
              sx={{
                mt: '5px',
                ml: '12px',
                fontWeight: 400,
                fontSize: 'H200',
                lineHeight: '14px',
                color: 't.black60',
              }}
            >
              Results capped at first 10,000 records
            </BoxV2>
          </Flex>
          <Flex sx={{ alignItems: 'center', gap: 's', mb: 'xs' }}>
            <FilterToggleButton
              visible={isFilterVisible}
              onClick={toggleFilterVisibility}
            />
            <Button
              variant="outline"
              onClick={handleDownload}
              data-testid="tableDownloadButton"
              style={{
                borderColor: '#FE772D',
                color: '#B2531F',
                padding: '12px 16px',
              }}
            >
              <FontAwesomeIcon icon={faArrowDownToLine} />
              Download
            </Button>
          </Flex>
          <Container maxWidth={false} style={{ padding: '0px' }}>
            {isFilterVisible && (
              <TableFilter
                portal="dca"
                configs={filter.configs}
                candidateValues={filter.candidateFilters}
                appliedValues={filter.appliedFilters}
                onCandidateValuesChange={filter.handleEdit}
                onAppliedValuesChange={filter.handleApply}
                data-testid="eventListTableFilter"
                className="filters"
              />
            )}
            <Flex
              sx={{
                flexDirection: 'column',
                justifyContent: 'center',
                mt: 'm',
              }}
            >
              <Table
                testId="eventListingTable"
                disableHover
                headRows={headRows}
                emptyState="no events to view"
                loading={table.loading}
                tableData={table.tableData.map((record, index) => (
                  <EventsListingRow
                    key={`${index}_${record.id}`}
                    event={record}
                  />
                ))}
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                filter={table.filter}
                pagination={table.pagination}
              />
              {isExceedingLimit ? (
                <div className="text-center" style={{ paddingTop: '10px' }}>
                  This page can only show the latest {MAX_EVENT_RECORDS} records
                  out of the total of {table.total}. Contact support for details
                  on your remaining records.
                </div>
              ) : null}
            </Flex>
          </Container>
        </>
      )}
    </section>
  );
};

export default EventsList;
